<template>
  <category-page-card
    v-if="categoryPage"
    :item="itemPackage"
    :item-width="itemWidth"
    :dialog="dialog"
    :hide-image="hideImage"
    :bg-color="bgColor"
    :text-color="textColor"
    :is-mobile="isMobile"
    :item-name="packageName"
    :text-center="textCenter"
    :item-count="itemCount(itemPackage)"
    :has-reduced-opacity="hasItemPackageReducedOpacity"
    :is-touching="isTouching"
    :badge-text-color="badgeTextColor"
    :display-price="displayPrice"
    :item-price="itemPrice"
    :has-extra-charge="false"
    :category-page="categoryPage"
    data-test-id="'package-card'"
    @touchstart="handleTouchStart"
    @click="handleClick"
  />
  <category-dialog-card
    v-else
    :item="itemPackage"
    :item-width="itemWidth"
    :dialog="dialog"
    :hide-image="hideImage"
    :bg-color="bgColor"
    :text-color="textColor"
    :is-mobile="isMobile"
    :item-name="packageName"
    :text-center="textCenter"
    :item-count="itemCount(itemPackage)"
    :has-reduced-opacity="hasItemPackageReducedOpacity"
    :is-touching="isTouching"
    :badge-text-color="badgeTextColor"
    :display-price="displayPrice"
    :item-price="itemPrice"
    :has-extra-charge="false"
    :category-page="categoryPage"
    data-test-id="'package-card'"
    @touchstart="handleTouchStart"
    @click="handleClick"
  >
    <template #alcohol-dialog>
      <alcohol-acknowledgement-dialog
        ref="alcoholAcknowledgementDialog"
        @alcohol-acknowledgement-concerned="checkPackageSalesRestrictions()"
      />
    </template>
  </category-dialog-card>
</template>

<script>
import { Cookies } from 'quasar'
import Order from 'src/models/order/Order'
import ItemPackage from 'src/models/package/ItemPackage'
import AlcoholAcknowledgementDialog from 'src/modules/checkout/components/AlcoholAcknowledgementDialog'
import CategoryDialogCard from 'src/modules/checkout/components/CategoryDialogCard'
import CategoryPageCard from 'src/modules/checkout/components/CategoryPageCard'
import { useSalesRestrictions } from 'src/modules/register/composables/sales-restrictions/sales-restrictions'
import { hasReducedOpacity } from 'src/services/inventory/sales-restrictions'

export default {
  name: 'PackageCard',
  components: {
    CategoryDialogCard,
    AlcoholAcknowledgementDialog,
    CategoryPageCard
  },
  context: ['currentUser'],
  configuration: ['currentCircuit', 'currentSite'],
  props: {
    itemPackage: {
      type: ItemPackage,
      required: true
    },
    order: {
      type: Order,
      required: false,
      default: null
    },
    displayPrice: {
      type: Boolean,
      required: false,
      default: true
    },
    itemWidth: {
      type: String,
      required: false,
      default: '18%'
    },
    dialog: {
      type: Boolean,
      required: false,
      default: true
    },
    hideImage: {
      type: Boolean,
      required: false,
      default: false
    },
    bgColor: {
      type: String,
      required: false,
      default: 'responsive'
    },
    textColor: {
      type: String,
      required: false,
      default: 'responsive'
    },
    isMobile: {
      type: Boolean,
      required: false,
      default: false
    },
    hasAlcoholAcknowledgement: {
      type: Boolean,
      required: false,
      default: false
    },
    packageName: {
      type: String,
      required: false,
      default: null
    },
    textCenter: {
      type: Boolean,
      required: false,
      default: false
    },
    categoryPage: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['click'],
  setup () {
    const { checkForSalesRestrictions } = useSalesRestrictions()

    return {
      checkForSalesRestrictions
    }
  },
  data () {
    return {
      itemPrice: null,
      isTouching: false
    }
  },
  computed: {
    badgeTextColor () {
      if (this.hideImage) return this.$isColorDark(this.currentSite.secondaryColor) ? 'white' : 'black'
      return this.$isColorDark(this.currentSite.primaryColor) ? 'white' : 'black'
    },
    inventoryStrategy () {
      return this.currentSite?.inventoryStrategy
    },
    hasItemPackageReducedOpacity () {
      if (!this.order) return false

      for (const packageItem of this.itemPackage.itemPackageItems.data) {
        if (packageItem.itemVariation) {
          const restricted = hasReducedOpacity(packageItem.itemVariation, {
            order: this.order
          })

          if (restricted) return true
        }
      }
      return false
    }
  },
  watch: {
    order: {
      handler () {
        this.calculatePrice()
      },
      immediate: true
    },
    'itemPackage.itemPackageItems.data': {
      handler () {
        this.calculatePrice()
      },
      deep: true
    }
  },
  mounted () {
    this.calculatePrice()
  },
  methods: {
    calculatePrice () {
      // Calculate price asynchronously
      if (this.displayPrice) {
        setTimeout(async () => {
          let totalPrice = 0
          for (const packageItem of this.itemPackage.itemPackageItems.data) {
            if (packageItem.itemVariation) {
              totalPrice += packageItem.itemVariation.price || 0
            }
          }
          this.itemPrice = totalPrice
        }, 0)
      }
    },
    handleClick (itemPackage) {
      const acknowledgeCookies = Cookies.get(this.$constants.cookies.alcoholAcknowledgement)
      const acknowledgeCookiesSiteId = acknowledgeCookies?.siteId

      const dontAskAgainConfirmed = !!acknowledgeCookies && acknowledgeCookiesSiteId === this.currentSite.id

      if (itemPackage.showAlcoholAcknowledgement && !dontAskAgainConfirmed && !this.$register) {
        this.$refs.alcoholAcknowledgementDialog.show()
      } else {
        this.checkPackageSalesRestrictions()
      }
    },
    checkPackageSalesRestrictions () {
      // Get all items in the package that need sales restriction checks
      const packageItems = this.itemPackage.itemPackageItems.data
      const itemsToCheck = packageItems.filter(item =>
        item.ticketType || item.itemVariation
      )

      if (itemsToCheck.length === 0) {
        // No items to check, proceed immediately
        this.$emit('click', false)
        return
      }

      // Check each item in the package sequentially
      this.checkNextItem(itemsToCheck, 0, false)
    },
    checkNextItem (itemsToCheck, index, adminApproved) {
      if (index >= itemsToCheck.length) {
        // All items have been checked successfully
        this.$emit('click', adminApproved)
        return
      }

      const packageItem = itemsToCheck[index]

      if (packageItem.itemVariation) {
        // Check item variation restrictions
        this.checkForSalesRestrictions(
          packageItem.itemVariation,
          { order: this.order },
          (itemAdminApproved = false) => {
            // Continue to next item with updated admin approval status
            this.checkNextItem(itemsToCheck, index + 1, adminApproved || itemAdminApproved)
          }
        )
      } else {
        // Skip items that don't need checking
        this.checkNextItem(itemsToCheck, index + 1, adminApproved)
      }
    },
    handleTouchStart () {
      this.isTouching = true
      setTimeout(() => {
        this.isTouching = false
      }, 500)
    },
    itemCount (itemPackage) {
      if (!this.order) return 0

      const packageItems = itemPackage.itemPackageItems.data
      const itemsPerPackage = packageItems.filter(item =>
        item.ticketType || item.itemVariation
      ).length

      if (itemsPerPackage === 0) return 0

      const orderItemsForPackage = this.order.orderItems.data.filter(orderItem =>
        orderItem.itemPackageId === itemPackage.id &&
    (orderItem.type === 'ticket' || orderItem.type === 'item')
      )

      if (orderItemsForPackage.length === 0) return 0

      return orderItemsForPackage.length / itemsPerPackage
    }
  }
}
</script>

<style scoped lang="scss">
.q-card {
  transition: all 0.2s ease-in-out;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
}
</style>
